import { getFrontifyImage } from '@/components/BatteryEcomm/PublicRetrofit/getFrontifyImage';
import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import type {
  SubscriptionBundleCF,
  SubscriptionOfferCF,
  SubscriptionProductCF,
} from '@/types';

const RASTER_IMAGE_EXTENSIONS = new Set([
  'avif',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'webp',
]);

export function isSubscriptionBundle(
  offer: SubscriptionOfferCF
): offer is SubscriptionBundleCF {
  return offer.sys.contentType.sys.id === 'subscriptionBundle';
}

/** The products an offer sells: the product itself, or a bundle's members. */
export function getOfferProducts(
  offer: SubscriptionOfferCF
): SubscriptionProductCF[] {
  if (!isSubscriptionBundle(offer)) {
    return [offer];
  }

  const seenIds = new Set<string>();
  const products: SubscriptionProductCF[] = [];

  for (const product of offer.fields.subscriptionProducts ?? []) {
    if (product && !seenIds.has(product.sys.id)) {
      seenIds.add(product.sys.id);
      products.push(product);
    }
  }

  return products;
}

/**
 * The Sunrun Membership an offer depends on, as authored in Contentful.
 * Offers without a `dependsOn` entry have no membership.
 */
export function getOfferMembership(
  offer: SubscriptionOfferCF
): SubscriptionProductCF | undefined {
  return offer.fields.dependsOn;
}

/**
 * The first image of a Frontify field, unless it is a known non-image file.
 * Frontify fields can also hold documents (e.g. a PDF), which the browser
 * cannot display as an image. An asset without an extension is rendered.
 */
export function getRasterImage(
  image: unknown
): FrontifyImageContent | undefined {
  const frontifyImage = getFrontifyImage(image);
  const extension = frontifyImage?.ext?.replace(/^\./, '').toLowerCase();

  return !extension || RASTER_IMAGE_EXTENSIONS.has(extension)
    ? frontifyImage
    : undefined;
}
